import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Plus, FolderKanban, Calendar, Users, Megaphone, Handshake, Pencil, Trash2,
  ChevronDown, ChevronUp, Link2, X, BarChart3,
} from "lucide-react";
import { toast } from "sonner";

const PHASE = {
  upcoming: { label: "Upcoming", cls: "bg-sky-100 text-sky-700" },
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-700" },
  completed: { label: "Completed", cls: "bg-zinc-200 text-zinc-700" },
};

const blank = { name: "", start_date: "", end_date: "", notes: "" };

export default function Campaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode:'create'|'edit', data}
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => Promise.all([
    api.get("/campaigns").then((r) => setCampaigns(r.data.campaigns)),
    api.get("/opportunities/mine").then((r) => setOpps(r.data.opportunities)).catch(() => {}),
  ]).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(blank); setDialog({ mode: "create" }); };
  const openEdit = (c) => { setForm({ name: c.name, start_date: c.start_date, end_date: c.end_date, notes: c.notes || "" }); setDialog({ mode: "edit", data: c }); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Please give the campaign a name"); return; }
    if (form.start_date && form.end_date && form.end_date < form.start_date) { toast.error("End date can't be before start date"); return; }
    setBusy(true);
    try {
      if (dialog.mode === "edit") await api.put(`/campaigns/${dialog.data.id}`, form);
      else await api.post("/campaigns", form);
      toast.success(dialog.mode === "edit" ? "Campaign updated" : "Campaign created");
      setDialog(null); load();
    } catch { toast.error("Could not save campaign"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    const c = deleting; setDeleting(null);
    try { await api.delete(`/campaigns/${c.id}`); toast.success("Campaign removed (opportunities kept)"); load(); }
    catch { toast.error("Could not remove campaign"); }
  };

  const toggleExpand = async (c) => {
    if (expanded === c.id) { setExpanded(null); return; }
    setExpanded(c.id); setDetail(null);
    try { const r = await api.get(`/campaigns/${c.id}`); setDetail(r.data); }
    catch { toast.error("Could not load campaign"); }
  };

  const linkOpp = async (cid, oid) => {
    try { const r = await api.post(`/campaigns/${cid}/link`, { opportunity_id: oid }); setDetail(r.data); load(); toast.success("Opportunity added"); }
    catch { toast.error("Could not add opportunity"); }
  };
  const unlinkOpp = async (cid, oid) => {
    try { const r = await api.post(`/campaigns/${cid}/unlink`, { opportunity_id: oid }); setDetail(r.data); load(); }
    catch { toast.error("Could not remove opportunity"); }
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";
  const unlinkedOpps = (cid) => opps.filter((o) => o.campaign_id !== cid);

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-zinc-900 text-white"><FolderKanban className="h-5 w-5" /></div>
              <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Campaigns</h1>
            </div>
            <p className="mt-2 text-zinc-500">Group opportunities and collaborations to measure their combined impact over time.</p>
          </div>
          <Button data-testid="create-campaign-btn" onClick={openCreate} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Plus className="mr-1 h-4 w-4" /> New campaign</Button>
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        ) : campaigns.length === 0 ? (
          <div data-testid="campaigns-empty" className="flex h-56 flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-300 text-center">
            <FolderKanban className="h-8 w-8 text-zinc-300" />
            <p className="mt-2 font-display text-lg font-semibold text-zinc-700">No campaigns yet</p>
            <p className="mt-1 max-w-sm text-sm text-zinc-500">Create a campaign, add your opportunities, and later see how your creator collaborations moved the needle.</p>
            <Button data-testid="create-campaign-empty" onClick={openCreate} className="mt-4 rounded-full bg-emerald-600 hover:bg-emerald-700"><Plus className="mr-1 h-4 w-4" /> New campaign</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {campaigns.map((c) => {
              const phase = PHASE[c.phase] || PHASE.active;
              const open = expanded === c.id;
              return (
                <div key={c.id} data-testid={`campaign-${c.id}`} className="rounded-2xl border border-zinc-200 bg-white">
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate font-display text-lg font-semibold text-zinc-900">{c.name}</p>
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${phase.cls}`}>{phase.label}</span>
                        </div>
                        <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-zinc-500">
                          <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmtDate(c.start_date)} – {fmtDate(c.end_date)}</span>
                        </p>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                          <span><Megaphone className="mr-1 inline h-3.5 w-3.5" />{c.opportunity_count} opportunit{c.opportunity_count !== 1 ? "ies" : "y"}</span>
                          <span><Handshake className="mr-1 inline h-3.5 w-3.5" />{c.collaboration_count} collaboration{c.collaboration_count !== 1 ? "s" : ""}</span>
                          <span><Users className="mr-1 inline h-3.5 w-3.5" />{c.creator_count} creator{c.creator_count !== 1 ? "s" : ""}</span>
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <button data-testid={`edit-campaign-${c.id}`} onClick={() => openEdit(c)} className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"><Pencil className="h-4 w-4" /></button>
                        <button data-testid={`delete-campaign-${c.id}`} onClick={() => setDeleting(c)} className="flex h-8 w-8 items-center justify-center rounded-full text-rose-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                    <button data-testid={`expand-campaign-${c.id}`} onClick={() => toggleExpand(c)} className="mt-3 flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-800">
                      {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} Manage & view creators
                    </button>
                  </div>

                  {open && (
                    <div data-testid={`campaign-detail-${c.id}`} className="border-t border-zinc-100 p-4">
                      {!detail || detail.id !== c.id ? (
                        <div className="flex h-16 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
                      ) : (
                        <div className="space-y-4">
                          {/* Opportunities */}
                          <div>
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">Opportunities in this campaign</p>
                            {detail.opportunities.length === 0 ? <p className="text-sm text-zinc-500">None yet — add one below.</p> : (
                              <div className="space-y-2">
                                {detail.opportunities.map((o) => (
                                  <div key={o.id} data-testid={`campaign-opp-${o.id}`} className="flex items-center gap-2 rounded-xl border border-zinc-100 bg-zinc-50/60 px-3 py-2">
                                    <Megaphone className="h-4 w-4 text-emerald-600" />
                                    <button onClick={() => navigate(`/business/opportunities/${o.id}`)} className="min-w-0 flex-1 truncate text-left text-sm font-medium text-zinc-800 hover:underline">{o.title}</button>
                                    <button data-testid={`unlink-opp-${o.id}`} onClick={() => unlinkOpp(c.id, o.id)} className="flex h-6 w-6 items-center justify-center rounded-full text-zinc-400 hover:bg-rose-50 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {unlinkedOpps(c.id).length > 0 && (
                              <div className="mt-2 flex items-center gap-2">
                                <Select onValueChange={(v) => linkOpp(c.id, v)} value="">
                                  <SelectTrigger data-testid={`link-opp-select-${c.id}`} className="h-10 flex-1"><SelectValue placeholder="Add an opportunity…" /></SelectTrigger>
                                  <SelectContent>
                                    {unlinkedOpps(c.id).map((o) => <SelectItem key={o.id} value={o.id}>{o.title || "Untitled"}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <Link2 className="h-4 w-4 text-zinc-400" />
                              </div>
                            )}
                          </div>

                          {/* Participating creators */}
                          <div>
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">Participating creators</p>
                            {detail.creators.length === 0 ? (
                              <p className="text-sm text-zinc-500">Creators appear here once they accept a collaboration in this campaign.</p>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {detail.creators.map((cr) => (
                                  <div key={cr.user_id} data-testid={`campaign-creator-${cr.user_id}`} className="flex items-center gap-2 rounded-full border border-zinc-200 bg-white py-1 pl-1 pr-3">
                                    <div className="h-7 w-7 overflow-hidden rounded-full bg-zinc-100">
                                      {cr.photo ? <img src={fileUrl(cr.photo)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-xs font-bold text-zinc-400">{cr.name?.[0]}</div>}
                                    </div>
                                    <span className="text-sm font-medium text-zinc-700">{cr.name}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-2 rounded-xl bg-indigo-50/60 p-3 text-xs text-indigo-700">
                            <BarChart3 className="h-4 w-4 shrink-0" />
                            Before/during/after performance comparison will appear here once your Instagram analytics are connected.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="campaign-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "edit" ? "Edit campaign" : "New campaign"}</DialogTitle>
            <DialogDescription>Give your campaign a name and the dates it runs. You can add opportunities afterwards.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Campaign name</Label><Input data-testid="campaign-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Summer Menu Launch" className="mt-1.5 h-11" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start date</Label><Input data-testid="campaign-start-input" type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} className="mt-1.5 h-11" /></div>
              <div><Label>End date</Label><Input data-testid="campaign-end-input" type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} className="mt-1.5 h-11" /></div>
            </div>
            <div><Label>Notes <span className="text-zinc-400">(optional)</span></Label><Input data-testid="campaign-notes-input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Goal or theme of this campaign" className="mt-1.5 h-11" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="campaign-save-btn" onClick={save} disabled={busy} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (dialog?.mode === "edit" ? "Save changes" : "Create campaign")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent data-testid="campaign-delete-dialog">
          <DialogHeader>
            <DialogTitle>Remove “{deleting?.name}”?</DialogTitle>
            <DialogDescription>This removes the campaign grouping only. Your opportunities and collaborations are kept and simply unlinked.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button data-testid="confirm-delete-campaign" onClick={remove} className="w-full rounded-full bg-rose-600 hover:bg-rose-700"><Trash2 className="mr-2 h-4 w-4" /> Remove campaign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MobileNav />
    </div>
  );
}
